import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Star, Send, CheckCircle, AlertCircle } from "lucide-react";
import { supportFeedbackService } from "@core/services/SupportFeedbackService";
import { useAuth } from "@core/contexts/AuthContext";

interface FeedbackCollectorProps {
  ticketId: string;
  ticketSubject: string;
  alreadySubmitted?: boolean;
}

export const FeedbackCollector: React.FC<FeedbackCollectorProps> = ({
  ticketId,
  ticketSubject,
  alreadySubmitted = false,
}) => {
  const { user } = useAuth();
  const { t } = useTranslation("forms");
  const [hoveredRating, setHoveredRating] = useState(0);
  const [selectedRating, setSelectedRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(alreadySubmitted);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ratingLabels: Record<number, string> = {
    1: t("support.feedback.ratingLabel.1"),
    2: t("support.feedback.ratingLabel.2"),
    3: t("support.feedback.ratingLabel.3"),
    4: t("support.feedback.ratingLabel.4"),
    5: t("support.feedback.ratingLabel.5"),
  };

  const activeRating = hoveredRating || selectedRating;

  const handleSubmit = async () => {
    if (!selectedRating || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await supportFeedbackService.createFeedback(
        ticketId,
        selectedRating,
        comment || undefined,
        user?.id,
      );
      setSubmitted(true);
    } catch (err) {
      setError(t("support.feedback.errorRetry"));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
        <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mb-6">
          <CheckCircle className="w-8 h-8 text-green-400" />
        </div>
        <h2 className="text-2xl font-semibold text-white mb-3">
          {t("support.feedback.thankYouTitle")}
        </h2>
        <p className="text-gray-400 max-w-sm">
          {t("support.feedback.thankYouText")}
        </p>
        {selectedRating > 0 && (
          <div className="mt-6 flex gap-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <Star
                key={s}
                className={`w-6 h-6 ${s <= selectedRating ? "text-yellow-400 fill-yellow-400" : "text-gray-600"}`}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full max-w-lg mx-auto">
      {/* Ticket subject */}
      <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4 mb-8">
        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">
          {t("support.feedback.ticketResolved")}
        </p>
        <p className="text-white font-medium">{ticketSubject}</p>
      </div>

      {/* Question */}
      <h2 className="text-xl font-semibold text-white mb-2 text-center">
        {t("support.feedback.question")}
      </h2>
      <p className="text-gray-400 text-sm text-center mb-8">
        {t("support.feedback.subtitle")}
      </p>

      {/* Stars */}
      <div className="flex justify-center gap-3 mb-3">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setSelectedRating(star)}
            onMouseEnter={() => setHoveredRating(star)}
            onMouseLeave={() => setHoveredRating(0)}
            className="transition-transform hover:scale-110 focus:outline-none"
            aria-label={t("support.feedback.starAria", { count: star })}
          >
            <Star
              className={`w-10 h-10 transition-colors ${
                star <= activeRating
                  ? "text-yellow-400 fill-yellow-400"
                  : "text-gray-600"
              }`}
            />
          </button>
        ))}
      </div>

      {/* Label */}
      <div className="text-center h-6 mb-6">
        {activeRating > 0 && (
          <span className="text-sm text-gray-300 font-medium">
            {ratingLabels[activeRating]}
          </span>
        )}
      </div>

      {/* Comment */}
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder={t("support.feedback.commentPlaceholder")}
        rows={3}
        className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 mb-4"
      />

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-red-400 text-sm mb-4">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Submit */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!selectedRating || submitting}
        className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-500 hover:to-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
      >
        {submitting ? (
          <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <Send className="w-4 h-4" />
        )}
        {submitting
          ? t("support.feedback.sending")
          : t("support.feedback.submit")}
      </button>
    </div>
  );
};
